import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { ChevronDown, Plus, Check, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export default function WorkspaceSwitcher() {
    const { t } = useTranslation();
    const { workspace, setWorkspace } = useAuthStore();
    const [isOpen, setIsOpen] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();

    // Fetch workspaces
    const { data, isLoading } = useQuery({
        queryKey: ['workspaces'],
        queryFn: () => api.getWorkspaces(),
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSwitchWorkspace = (ws: { id: string; name: string; slug: string }) => {
        setWorkspace(ws);
        setIsOpen(false);
        // Invalidate queries to refetch data for new workspace
        queryClient.invalidateQueries();
    };

    const workspaces = data?.workspaces || [];

    return (
        <>
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex items-center justify-between p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                >
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-600/20 text-primary-600 rounded-lg flex items-center justify-center font-semibold text-sm">
                            {workspace?.name?.charAt(0)?.toUpperCase() || 'W'}
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-medium truncate max-w-[140px]">
                                {workspace?.name || 'Workspace'}
                            </p>
                            <p className="text-xs text-neutral-500">
                                {workspaces.length > 1 ? `${workspaces.length} workspaces` : 'Personal'}
                            </p>
                        </div>
                    </div>
                    <ChevronDown className={clsx('w-4 h-4 text-neutral-400 transition-transform', isOpen && 'rotate-180')} />
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 overflow-hidden">
                        {isLoading ? (
                            <div className="p-4 text-center">
                                <Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" />
                            </div>
                        ) : (
                            <>
                                <div className="max-h-48 overflow-y-auto">
                                    {workspaces.map((ws) => (
                                        <button
                                            key={ws.id}
                                            onClick={() => handleSwitchWorkspace({ id: ws.id, name: ws.name, slug: ws.slug })}
                                            className={clsx(
                                                'w-full flex items-center justify-between p-3 hover:bg-neutral-50 dark:hover:bg-neutral-700 text-left',
                                                ws.id === workspace?.id && 'bg-primary-50 dark:bg-primary-600/10'
                                            )}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 bg-primary-100 dark:bg-primary-600/20 text-primary-600 rounded flex items-center justify-center text-xs font-semibold">
                                                    {ws.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">{ws.name}</p>
                                                    <p className="text-xs text-neutral-500">
                                                        {ws.member_count} {t('team.members', 'thành viên')} • {ws.role}
                                                    </p>
                                                </div>
                                            </div>
                                            {ws.id === workspace?.id && (
                                                <Check className="w-4 h-4 text-primary-600" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <div className="border-t border-neutral-200 dark:border-neutral-700">
                                    <button
                                        onClick={() => {
                                            setIsOpen(false);
                                            setShowCreateModal(true);
                                        }}
                                        className="w-full flex items-center gap-2 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-700 text-primary-600"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span className="text-sm font-medium">{t('workspace.create', 'Tạo workspace mới')}</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Create Workspace Modal */}
            {showCreateModal && (
                <CreateWorkspaceModal onClose={() => setShowCreateModal(false)} />
            )}
        </>
    );
}

function CreateWorkspaceModal({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const { setWorkspace } = useAuthStore();
    const queryClient = useQueryClient();
    const [name, setName] = useState('');

    const createMutation = useMutation({
        mutationFn: () => api.createWorkspace(name),
        onSuccess: (data) => {
            if (data.workspace) {
                setWorkspace({
                    id: data.workspace.id,
                    name: data.workspace.name,
                    slug: data.workspace.slug,
                });
                queryClient.invalidateQueries({ queryKey: ['workspaces'] });
                queryClient.invalidateQueries();
            }
            onClose();
        },
    });

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-lg font-semibold">{t('workspace.create', 'Tạo workspace mới')}</h2>
                    <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        createMutation.mutate();
                    }}
                    className="p-4 space-y-4"
                >
                    <div>
                        <label className="label">{t('workspace.name', 'Tên workspace')}</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input"
                            placeholder={t('workspace.namePlaceholder', 'VD: Phòng Marketing, Team Dev...')}
                            required
                            minLength={2}
                        />
                    </div>

                    {createMutation.isError && (
                        <div className="p-3 bg-danger-50 text-danger-600 text-sm rounded-lg">
                            {t('common.error', 'Có lỗi xảy ra. Vui lòng thử lại.')}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-secondary">
                            {t('common.cancel', 'Hủy')}
                        </button>
                        <button type="submit" disabled={createMutation.isPending || !name.trim()} className="btn-primary">
                            {createMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    <Plus className="w-4 h-4" />
                                    {t('common.create', 'Tạo')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
